import i18n from '../../i18n';
import type { ApiError } from '../../types/auth';

export class AuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthError';
  }
}

export class ApiRequestError extends Error {
  errors?: string[];
  status?: number;

  constructor(message: string, errors?: string[], status?: number) {
    super(message);
    this.name = 'ApiRequestError';
    this.errors = errors;
    this.status = status;
  }
}

// In development: proxy to localhost:3000 (configured in package.json)
// In production: the same-origin default `/api/v1` reaches the
// backend through the frontend Nginx `/api/` proxy. Set
// REACT_APP_API_URL only for intentionally split frontend/API origins
// (e.g. local development pointing at a remote backend).
const API_URL = process.env.REACT_APP_API_URL || '/api/v1';

export class ApiClient {
  baseURL: string;

  constructor() {
    this.baseURL = API_URL;
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  setToken(token: string): void {
    localStorage.setItem('token', token);
  }

  removeToken(): void {
    localStorage.removeItem('token');
  }

  async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const token = this.getToken();
    const language = i18n.language || 'en-US';
    
    const headers: HeadersInit = {
      ...options.headers,
      'Accept-Language': language,
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };

    if (options.body && !(options.body instanceof FormData)) {
      (headers as Record<string, string>)['Content-Type'] = 'application/json';
    }

    const config: RequestInit = {
      ...options,
      headers,
    };

    // Convert body to JSON if not FormData
    if (config.body && !(config.body instanceof FormData) && typeof config.body === 'object') {
      config.body = JSON.stringify(config.body);
    }

    const response = await fetch(url, config);

    if (!response.ok) {
      const errorData = await this.parseErrorResponse(response);
      const errorMessage = errorData?.error || 'Request failed';

      if (response.status === 401) {
        throw new AuthError(errorMessage);
      }

      throw new ApiRequestError(errorMessage, errorData?.errors, response.status);
    }

    const data = await this.parseJsonResponse(response);

    return data as T;
  }

  private async parseJsonResponse(response: Response): Promise<unknown> {
    const responseText = await response.text();

    if (!responseText) {
      return null;
    }

    return JSON.parse(responseText);
  }

  // A proxy layer (nginx) can answer with an HTML page instead of JSON when it
  // rejects a request — for example a 413 body-limit response. Read the error
  // body defensively so a non-JSON payload never surfaces as a raw
  // SyntaxError from JSON.parse; callers get a typed ApiRequestError carrying
  // the HTTP status instead.
  private async parseErrorResponse(response: Response): Promise<ApiError | null> {
    const responseText = await response.text();

    if (!responseText) {
      return null;
    }

    try {
      return JSON.parse(responseText) as ApiError;
    } catch {
      return null;
    }
  }

  async downloadAdminQuoteAttachment(quoteId: number | string, attachmentId: number | string): Promise<void> {
    const url = `${this.baseURL}/admin/cargo_quote_requests/${quoteId}/attachments/${attachmentId}/download`;
    const token = this.getToken();
    const language = i18n.language || 'en-US';

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept-Language': language,
        ...(token && { 'Authorization': `Bearer ${token}` }),
      }
    });

    if (!response.ok) {
      // best effort parse
      const err = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new ApiRequestError(err.error || 'Request failed', err.errors);
    }

    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^";]+)"?/i);
    const filename = match?.[1] || `attachment-${attachmentId}`;

    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(objectUrl);
  }

  async downloadAllAdminQuoteAttachments(quoteId: number | string): Promise<void> {
    const url = `${this.baseURL}/admin/cargo_quote_requests/${quoteId}/attachments/download_all`;
    const token = this.getToken();
    const language = i18n.language || 'en-US';

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept-Language': language,
        ...(token && { 'Authorization': `Bearer ${token}` }),
      }
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new ApiRequestError(err.error || 'Request failed', err.errors);
    }

    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^";]+)"?/i);
    const filename = match?.[1] || `quote-${quoteId}-attachments.zip`;

    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(objectUrl);
  }

  // Carrier Directory template (issues #176, #178). Streams the
  // backend-owned CSV template to the admin's browser as a download.
  // Accept-Language is forwarded so the spreadsheet opens in the
  // admin's locale (the backend resolves header labels and example
  // values through the request locale, issue #176). The filename is
  // read from Content-Disposition when present so a backend filename
  // change does not require a frontend change.
  async downloadAdminCarrierTemplate(): Promise<void> {
    const url = `${this.baseURL}/admin/transportation_companies/template`;
    const token = this.getToken();
    const language = i18n.language || 'en-US';

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept-Language': language,
        ...(token && { 'Authorization': `Bearer ${token}` }),
      }
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new ApiRequestError(err.error || 'Request failed', err.errors);
    }

    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^";]+)"?/i);
    const filename = match?.[1] || 'carriers-template.csv';

    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(objectUrl);
  }

  // Carrier Directory import guide (PDF). Mirrors the template
  // downloader: same blob/Content-Disposition flow, same auth
  // headers, and the filename is read from Content-Disposition when
  // present so a backend filename change does not require a frontend
  // change. The default filename is the PDF guide.
  async downloadAdminCarrierGuide(): Promise<void> {
    const url = `${this.baseURL}/admin/transportation_companies/guide`;
    const token = this.getToken();
    const language = i18n.language || 'en-US';

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept-Language': language,
        ...(token && { 'Authorization': `Bearer ${token}` }),
      }
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new ApiRequestError(err.error || 'Request failed', err.errors);
    }

    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^";]+)"?/i);
    const filename = match?.[1] || 'carrier-csv-import-guide.pdf';

    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(objectUrl);
  }
}

export const api = new ApiClient();
